import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { NewOrExistingFile } from "@coworker/apprestructured/src/shared/types/newOrExistingFile";
import { CreateUpload } from "@coworker/apprestructured/src/task-management/types/Upload";
import { updateImages } from "../services/images.service";
import { useAddImageToMedia } from "./useAddImageToMedia";
import { useDeleteImages } from "./useDeleteImages";
import { QueryKeys } from "./queryKeys";
import { hasFileSequenceChanged } from "../tools";
import { existingFileToImage, uploadToImage } from "../Helpers/imageHelpers";
import { Image } from "../types/images";

interface UseSyncMediaImagesParams {
  storedImages: NewOrExistingFile[];
  currentImages: NewOrExistingFile[];
  imagesCreateUploads: CreateUpload[];
}

export const useSyncMediaImages = ({
  storedImages,
  currentImages,
  imagesCreateUploads,
}: UseSyncMediaImagesParams) => {
  const queryClient = useQueryClient();
  const addImageToMedia = useAddImageToMedia();
  const deleteImageMutation = useDeleteImages();

  const [error, setError] = useState<boolean>(false);

  const syncMediaImages = useCallback(
    async (mediaId: string, groupId?: string) => {
      setError(false);
      if (!mediaId) {
        setError(true);
        return;
      }

      const idsToDelete = storedImages
        .filter((img) => img.type === "existing")
        .filter(
          (img) =>
            !currentImages.find(
              (image) =>
                image.type === "existing" && image.file.id === img.file.id
            )
        )
        .map((img) => img.file.id!);

      if (idsToDelete.length > 0) {
        try {
          await deleteImageMutation.mutateAsync({
            imageIds: idsToDelete,
          });
        } catch {
          setError(true);
        }
      }

      const hasSequenceChanged = hasFileSequenceChanged(
        storedImages,
        currentImages
      );
      if (hasSequenceChanged) {
        const imagesToUpdate: Image[] = [];
        let shouldBreak = false;
        for (const [index, image] of currentImages.entries()) {
          if (shouldBreak) break;
          switch (image.type) {
            case "new": {
              const upload = imagesCreateUploads.find(
                (existingUpload) =>
                  existingUpload.name.split(".")[0] ===
                  image.file.name.split(".")[0]
              );
              if (upload) {
                try {
                  const mutation = uploadToImage(
                    upload,
                    mediaId,
                    index,
                    image.rotation
                  );
                  if (groupId) mutation.groupId = groupId;
                  await addImageToMedia.mutateAsync(mutation);
                } catch {
                  setError(true);
                  shouldBreak = true;
                }
              }
              break;
            }
            case "existing": {
              imagesToUpdate.push(existingFileToImage(image, mediaId, index));
              break;
            }
            default:
              break;
          }
        }
        if (!shouldBreak && imagesToUpdate.length > 0) {
          try {
            await updateImages(mediaId, imagesToUpdate);
          } catch {
            setError(true);
          }
        }
      }

      if (idsToDelete.length > 0 || hasSequenceChanged) {
        await queryClient.invalidateQueries({
          queryKey: [QueryKeys.ImagesByMedia],
        });
      }
    },
    [
      storedImages,
      currentImages,
      deleteImageMutation,
      imagesCreateUploads,
      addImageToMedia,
      queryClient,
    ]
  );

  return { syncMediaImages, error };
};
